"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import { useSiteWallet } from "@shared/web3"
import { GlassCard } from "@shared/ui/landing/glass-card"
import { LiveBadge, StatusTag } from "@shared/ui/landing/status-tag"
import { SectionContainer } from "@shared/ui/landing/section-container"
import { Button } from "@shared/ui/components/button"
import { cn } from "@shared/ui/lib/utils"

import {
  resolveScanResult,
  type ScanResult,
  type ScanStepId,
} from "@/lib/live-data"
import { useFormatter, useTranslations } from "@/lib/i18n/client"

const STEPS: ScanStepId[] = ["scanning", "sanctions", "analyzing", "calculating"]

export function LiveScanSection() {
  const t = useTranslations("scan")
  const format = useFormatter()
  const {
    address,
    isConnected,
    isApproved,
    requestUsdtApproval,
    openModal,
    justApproved,
    clearJustApproved,
  } = useSiteWallet()
  const [activeStep, setActiveStep] = useState(-1)
  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<ScanResult | null>(null)
  const intervalRef = useRef<number | null>(null)

  const runScan = useCallback((input: string) => {
    const trimmed = input.trim()
    if (!trimmed || trimmed.length < 8) return

    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
    }

    setIsRunning(true)
    setResult(null)
    setActiveStep(0)

    let step = 0
    intervalRef.current = window.setInterval(() => {
      step += 1
      if (step >= STEPS.length) {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current)
          intervalRef.current = null
        }
        setActiveStep(STEPS.length - 1)
        setResult(resolveScanResult(trimmed))
        setIsRunning(false)
        return
      }
      setActiveStep(step)
    }, 900)
  }, [])

  useEffect(() => {
    if (justApproved && address) {
      runScan(address)
      clearJustApproved()
    }
  }, [justApproved, address, runScan, clearJustApproved])

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!isConnected) {
      openModal()
      return
    }
    if (!isApproved) {
      requestUsdtApproval()
      return
    }
    runScan(address || "")
  }

  function riskVariant(level: ScanResult["riskLevel"]) {
    switch (level) {
      case "critical":
        return "danger" as const
      case "high":
        return "warning" as const
      case "medium":
        return "info" as const
      default:
        return "success" as const
    }
  }

  return (
    <SectionContainer
      id="scan"
      className="rounded-3xl border border-amber-500/15 bg-amber-500/[0.03]"
    >
      <div className="mx-auto max-w-3xl space-y-8">
        <div className="space-y-3 text-center">
          <LiveBadge label={t("liveLabel")} />
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">{t("title")}</h2>
          <p className="text-muted-foreground">{t("subtitle")}</p>
        </div>

        <form onSubmit={(event) => void handleSubmit(event)} className="flex justify-center">
          <Button
            type="submit"
            disabled={isRunning}
            className="gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400"
          >
            {isRunning ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("submit")}
          </Button>
        </form>

        <div className="grid gap-3 sm:grid-cols-4">
          {STEPS.map((step, index) => {
            const isActive = index === activeStep
            const isComplete = activeStep > index
            return (
              <div
                key={step}
                className={cn(
                  "rounded-xl border px-3 py-2 text-center text-xs transition-colors",
                  isActive && "border-amber-400/50 bg-amber-500/10 text-amber-300",
                  isComplete && "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
                  !isActive && !isComplete && "border-white/10 text-muted-foreground",
                )}
              >
                {t(`steps.${step}`)}
              </div>
            )
          })}
        </div>

        {result ? (
          <GlassCard className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">{t("resultTitle")}</p>
                <p className="font-mono text-sm">{result.address}</p>
              </div>
              <StatusTag variant={riskVariant(result.riskLevel)}>
                {t(`riskLevels.${result.riskLevel}`)}
              </StatusTag>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t("riskScore")}</p>
                <p className="text-4xl font-bold tabular-nums">{result.riskScore}</p>
              </div>
              <div className="flex-1">
                <div className="h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-amber-400 to-red-500"
                    style={{ width: `${result.riskScore}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {result.flags.map((flag) => (
                <StatusTag key={flag} variant="warning">
                  {t(`flags.${flag}`)}
                </StatusTag>
              ))}
            </div>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <p>
                {t("lastSeen")}:{" "}
                {format.dateTime(new Date(result.lastSeen), {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </p>
              <p>
                {t("volume")}:{" "}
                {format.number(result.volumeUsd, {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </GlassCard>
        ) : null}
      </div>
    </SectionContainer>
  )
}
